import Icon from '@/components/Icon'
import RichText from '@/components/RichText'
import { useKontentHomeConfig } from '@/components/contexts/KontentHomeConfigProvider'
import { useWebsiteConfiguration } from '@/components/contexts/WebsiteConfigurationProvider'
import { GridCol } from '@/components/nsw/grid/GridCol'
import { TREE_OPTIONS_DOWNLOAD_CUSTOM_VIEW } from '@/constants/treepickeroptions'
import {
	useLanguagesBasedOnSyllabuses,
	useSyllabusesThatHaveLanguages,
} from '@/hooks/useLanguagesBasedOnSyllabuses'
import { TaxoLanguage, TaxoSyllabus } from '@/kontent/taxonomies'
import { LanguagesPayload } from '@/layouts/wp_custom_view'
import { LanguagePicker } from '@/legacy-ported/components/custom/LanguagePicker'
import { downloadDocs } from '@/utils/downloadDocs'
import { getSyllabusTitleWithPublicationYear } from '@/utils/syllabus/getSyllabusTitleWithPublicationYear'
import type { Elements } from '@kontent-ai/delivery-sdk'
import Mustache from 'mustache'
import { useRouter } from 'next/router'
import { Button } from 'nsw-ds-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import CustomModal from '../../legacy-ported/components/base/CustomModal'
import GeneratingOverlayWordPdf from '../../legacy-ported/components/document/overlay/GeneratingOverlayWordPdf'
import {
	CommonSyllabusModal,
	CommonSyllabusModalProps,
	ViewSelection,
} from './CommonSyllabusModal'

export type DownloadViewModalProps = Omit<
	CommonSyllabusModalProps,
	'onConfirm' | 'title' | 'syllabusElementsOptions'
> & {
	// to set the downloadDocs preview mode param
	isPreviewMode?: boolean
	languagePickerTitle?: string
	pageType?: string
	/**
	 * Pre-computed modal title. When provided, used instead of deriving the
	 * title from props.syllabuses. Useful for callers (e.g. CEC download
	 * button) that know the title but don't pass the full Syllabus array.
	 */
	titleOverride?: string
}

type DownloadModalViewSelection = ViewSelection & {
	isPdf: boolean
	languages?: Record<string, TaxoLanguage[]>
}

export const DownloadViewModal = (props: DownloadViewModalProps) => {
	const refAbortController = useRef<AbortController>(null)
	const [generatingStatus, setGenerating] = useState(false)
	const [errorPopupMessage, setErrorPopupMessage] = useState('')
	const [selectedLanguages, setSelectedLanguages] = useState<
		Record<TaxoSyllabus | string, TaxoLanguage[]>
	>({})
	const router = useRouter()

	// Get website configuration for dialog message and mappings for URL resolution
	const websiteConfiguration = useWebsiteConfiguration()
	const { mappings, pageResponseLinkedItems } = useKontentHomeConfig()

	// Check if current syllabus is year K curriculum
	const isYearKCurriculum = useMemo(() => {
		if (!props.selectedSyllabuses?.length) return false
		const selectedSyllabus = props.syllabuses.find(
			(syl) => syl.system.codename === props.selectedSyllabuses[0],
		)
		if (!selectedSyllabus) return false

		// Check syllabus title for K- pattern (like "English K-10")
		const titleHasK =
			selectedSyllabus.elements?.title?.value?.match(/K-\d+|K–\d+/i)

		// Also check stage years for K- pattern
		const stageYearsHaveK =
			selectedSyllabus.elements?.stages__stage_years?.value?.some(
				(year) => year.name?.match(/K-\d+|K–\d+/i),
			)

		return !!(titleHasK || stageYearsHaveK)
	}, [props.selectedSyllabuses, props.syllabuses])

	// Disable Access Points for non-year K curriculum, Tags for non-English/Math K-10, and Examples based on syllabus
	const modifiedTreeOptions = useMemo(() => {
		// Detect if current syllabus is English K-10 or Mathematics K-10
		const selectedSyllabusCodename = props.selectedSyllabuses?.[0]
		const shouldEnableTags = selectedSyllabusCodename
			? selectedSyllabusCodename.includes('english_k_10') ||
			  selectedSyllabusCodename.includes('mathematics_k_10')
			: false

		// Check if selected syllabus has examples
		const selectedSyllabus = selectedSyllabusCodename
			? props.syllabuses.find(
					(syl) => syl.system.codename === selectedSyllabusCodename,
			  )
			: null
		const shouldEnableExamples =
			!!selectedSyllabus?.elements?.has_examples_in?.value?.length

		return TREE_OPTIONS_DOWNLOAD_CUSTOM_VIEW.map((option) => {
			// Handle tags disabling logic
			if (option.id === 'tags') {
				return {
					...option,
					disabled: !shouldEnableTags,
				}
			}

			if (option.id === 'access-points') {
				return {
					...option,
					disabled: !isYearKCurriculum,
				}
			}
			if (option.id === 'examples') {
				return {
					...option,
					disabled: !shouldEnableExamples,
				}
			}

			// Handle all-content children (access-points, examples, etc.)
			// if (option.id === 'all-content' && option.children) {
			// 	const modifiedChildren = option.children.map((child) => {
			// 		if (child.id === 'access-points') {
			// 			return {
			// 				...child,
			// 				disabled: !isYearKCurriculum,
			// 			}
			// 		}
			// 		if (child.id === 'examples') {
			// 			return {
			// 				...child,
			// 				disabled: !shouldEnableExamples,
			// 			}
			// 		}
			// 		return child
			// 	})

			// 	// Check if all children are disabled to disable parent
			// 	const allChildrenDisabled = modifiedChildren.every(
			// 		(child) => child.disabled,
			// 	)

			// 	return {
			// 		...option,
			// 		children: modifiedChildren,
			// 		disabled: allChildrenDisabled,
			// 	}
			// }
			return option
		})
	}, [isYearKCurriculum, props.selectedSyllabuses, props.syllabuses])

	const refSelection = useRef<DownloadModalViewSelection>({
		syllabuses: [],
		stages: [],
		tabs: [],
		isPdf: false,
		languages: {},
	})

	const { languageOptions, isShowLanguageOptions, toggleLanguageOptions } =
		useLanguagesBasedOnSyllabuses({
			selectedSyllabusCodenames: props.selectedSyllabuses,
			syllabuses: props.syllabuses,
		})
	const syllabusesThatHaveLanguages = useSyllabusesThatHaveLanguages(
		props.syllabuses,
	)

	// Generate dynamic title based on selected syllabus
	const modalTitle = useMemo(() => {
		if (props.selectedSyllabuses?.length === 1) {
			const selectedSyllabus = props.syllabuses.find(
				(syl) => syl.system.codename === props.selectedSyllabuses[0],
			)
			if (selectedSyllabus) {
				return getSyllabusTitleWithPublicationYear(
					selectedSyllabus,
					false,
				)
			}
		}
		return 'Please select syllabus content'
	}, [props.selectedSyllabuses, props.syllabuses])

	// Generate custom dialog message from website configuration using RichText component
	const customDialogMessage = useMemo(() => {
		if (
			!websiteConfiguration?.item?.elements
				?.message_download_syllabus_dialog?.value
		) {
			return null
		}

		const syllabusCodename = props.selectedSyllabuses?.[0]
		const selectedSyllabus = syllabusCodename
			? props.syllabuses.find(
					(syl) => syl.system.codename === syllabusCodename,
			  )
			: null

		// Get the original rich text element from website configuration
		const originalRichTextElement =
			websiteConfiguration.item.elements.message_download_syllabus_dialog

		// Get template from website configuration
		const template = originalRichTextElement.value

		// Prepare data for Mustache template
		const templateData = {
			syllabus: selectedSyllabus || {},
		}

		// Render template with Mustache
		const renderedTemplate = Mustache.render(template, templateData)

		// Create a rich text element from the rendered template, preserving links from original
		const richTextElement: Elements.RichTextElement = {
			type: 'rich_text' as any,
			name: 'message_download_syllabus_dialog',
			value: renderedTemplate,
			linkedItemCodenames:
				originalRichTextElement.linkedItemCodenames || [],
			linkedItems: originalRichTextElement.linkedItems || [],
			links: originalRichTextElement.links || [],
			images: originalRichTextElement.images || [],
		}

		// Prepare link-specific query parameters for custom download page link
		const linkQueryParams: [string, string][] = syllabusCodename
			? [
					[
						'custom_download_page_link',
						`syllabus-code=${syllabusCodename}`,
					],
			  ]
			: []

		// Use RichText component to properly process links and content
		return (
			<RichText
				richTextElement={richTextElement}
				linkedItems={pageResponseLinkedItems}
				mappings={mappings}
				currentSyllabus={selectedSyllabus}
				linkQueryParams={linkQueryParams}
			/>
		)
	}, [
		websiteConfiguration,
		props.selectedSyllabuses,
		props.syllabuses,
		mappings,
		pageResponseLinkedItems,
	])

	// Generate slotBeforeSearch content from website configuration
	const slotBeforeSearchContent = useMemo(() => {
		// If no website configuration field exists, return default content
		const richTextElement =
			websiteConfiguration?.item?.elements?.message_select_language_dialog
		if (!richTextElement?.value) {
			return (
				<p>
					Language selection only applies to language specific
					examples.
				</p>
			)
		}

		// Use RichText component to properly process links and content
		return (
			<RichText
				richTextElement={richTextElement}
				linkedItems={pageResponseLinkedItems}
				mappings={mappings}
				currentSyllabus={
					props.selectedSyllabuses?.[0]
						? props.syllabuses.find(
								(syl) =>
									syl.system.codename ===
									props.selectedSyllabuses[0],
						  )
						: null
				}
			/>
		)
	}, [
		websiteConfiguration,
		props.selectedSyllabuses,
		props.syllabuses,
		mappings,
		pageResponseLinkedItems,
	])

	const closeModal = () => {
		setGenerating(false)
	}

	const handleCancelGeneratingWordPdf = () => {
		refAbortController.current.abort()
		refAbortController.current = null
		closeModal()
	}

	const handleDownload = async (selection: DownloadModalViewSelection) => {
		refAbortController.current = new AbortController()
		setGenerating(true)
		const languages = selection.languages
			? Object.entries(selection.languages).flatMap(
					([syllabusTaxo, languages]) => {
						return languages.map((languageTaxo) => {
							return {
								languageTaxo,
								syllabusTaxo,
							}
						}) as LanguagesPayload[]
					},
			  )
			: []

		const [_, errorMessage] = await downloadDocs(
			{
				stages: selection.stages,
				syllabuses: selection.syllabuses,
				tabs: [...selection.tabs, 'curriculum-connections'],
				pdf: selection.isPdf,
				tags: [],
				isPreviewMode: props.isPreviewMode || false,
				languages,
			},
			refAbortController.current.signal,
			router.asPath,
			props.pageType,
		)

		if (refAbortController.current?.signal?.aborted) return
		if (errorMessage) {
			setErrorPopupMessage(errorMessage)
		}

		closeModal()
	}

	useEffect(() => {
		setGenerating(false)
		setErrorPopupMessage('')
	}, [])

	return (
		<>
			<CommonSyllabusModal
				{...props}
				title={'Download ' + (props.titleOverride ?? modalTitle)}
				isDownload={true}
				downloadModalText={customDialogMessage}
				onConfirm={(selection, _e, isPDF) => {
					refSelection.current = {
						...selection,
						isPdf: isPDF,
					}

					const _selectedSyllabusesHaveLanguages =
						syllabusesThatHaveLanguages.filter((syl) =>
							selection.syllabuses.includes(syl.system.codename),
						)
					if (
						_selectedSyllabusesHaveLanguages.length &&
						selection.tabs.includes('examples')
					) {
						return toggleLanguageOptions()
					}
					handleDownload(refSelection.current)
				}}
				customModalSlotActions={({ handleConfirm }) => {
					return (
						<>
							<GridCol xs="auto">
								<Button
									onClick={(e) => handleConfirm(e, false)}
									data-action="downloadword"
									css={{
										'@media (max-width: 575px)': {
											'&.nsw-button.nsw-button': {
												minWidth: '0',
												paddingLeft: '18px',
												paddingRight: '18px',
											},
										},
									}}
								>
									<span className="mr-2">
										Download as Word
									</span>
									<Icon icon="bxs:download" />
								</Button>
							</GridCol>
							<GridCol xs="auto">
								<Button
									onClick={(e) => handleConfirm(e, true)}
									data-action="downloadpdf"
									css={{
										'@media (max-width: 575px)': {
											'&.nsw-button.nsw-button': {
												minWidth: '0',
												paddingLeft: '18px',
												paddingRight: '18px',
											},
										},
									}}
								>
									<span className="mr-2">
										Download as PDF
									</span>
									<Icon icon="bxs:download" />
								</Button>
							</GridCol>
						</>
					)
				}}
				syllabusElementsOptions={modifiedTreeOptions}
				css={{
					'& .CustomModal__button-cancel.CustomModal__button-cancel':
						{
							minWidth: '0',
							paddingLeft: 18,
							paddingRight: 18,
						},
				}}
				hideConfirmButton
			/>
			<GeneratingOverlayWordPdf
				modalStatus={generatingStatus}
				handleCancel={handleCancelGeneratingWordPdf}
			/>
			<LanguagePicker
				syllabuses={syllabusesThatHaveLanguages.filter((syl) =>
					refSelection.current.syllabuses.includes(
						syl.system.codename,
					),
				)}
				languages={languageOptions}
				initialSelectedLanguages={selectedLanguages}
				title={modalTitle}
				modalStatus={isShowLanguageOptions}
				handleCancel={() => {
					toggleLanguageOptions()
				}}
				handleConfirm={async (languages) => {
					refSelection.current = {
						...refSelection.current,
						languages,
					}
					setSelectedLanguages(languages)
					handleDownload(refSelection.current)
					toggleLanguageOptions()
				}}
				confirmButtonText="Continue to download"
				slotBeforeSearch={slotBeforeSearchContent}
				isValidationNotRequired
			></LanguagePicker>
			{errorPopupMessage && (
				<CustomModal
					title="Error"
					modalStatus={!!errorPopupMessage}
					handleCancel={() => setErrorPopupMessage('')}
					hideConfirmButton
				>
					<p>{errorPopupMessage}</p>
				</CustomModal>
			)}
		</>
	)
}
